import React, { useState } from 'react';
import moment from 'moment';

const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '13px',
    color: '#1f2937',
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
};

/** Column widths for flight breakdown view: Flight, Sched Dep, Req Boarding, First Resource, First Boarding, Earliest Alloc, Ready, Inbound, Pax, Pass, Reason, Action */
const columnWidthsFlight = ['8%', '10%', '10%', '11%', '11%', '11%', '7%', '10%', '5%', '6%', '8%', '5%'];
/** Column widths for passenger breakdown: + Passenger Name, Passenger ID, Assistance */
const columnWidthsPassenger = ['9%', '9%', '6%', '6%', '7%', '8%', '8%', '8%', '7%', '5%', '5%', '7%', '5%'];

const ColGroup = ({ widths }) => (
    <colgroup>
        {(widths || columnWidthsFlight).map((width, i) => (
            <col key={i} style={{ width }} />
        ))}
    </colgroup>
);

const subTitleStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '6px',
    marginTop: '14px',
};

const formatTime = (isoString) => {
    if (!isoString) return '-';
    return moment(isoString).format('HH:mm');
};

const modalOverlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
};

const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '320px',
    maxWidth: '90vw',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
};

/**
 * Modal to set manual pass or fail override for a boarding flight.
 */
const BoardingOverrideModal = ({ overrideContext, open, onClose, onApply, isPatching }) => {
    const [action, setAction] = React.useState('pass');
    const [reason, setReason] = React.useState('');

    React.useEffect(() => {
        if (open && overrideContext) {
            setAction('pass');
            setReason('');
        }
    }, [open, overrideContext]);

    if (!open) return null;

    const trimmedReason = reason?.trim();
    const canApply = !!trimmedReason;

    const handleApply = () => {
        if (!overrideContext || !onApply || !trimmedReason) return;
        if (action === 'ignore') {
            onApply(overrideContext.row, null, trimmedReason, { ignore: true });
        } else {
            onApply(overrideContext.row, action === 'pass', trimmedReason);
        }
    };

    return (
        <div style={modalOverlayStyle} onClick={isPatching ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="boarding-override-title">
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                <h2 id="boarding-override-title" style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
                    Boarding SLA Override
                </h2>
                <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280' }}>
                    Manually set pass, fail, or ignore for {overrideContext?.row?.passengerName || overrideContext?.row?.flightNumber || 'this passenger'}
                    {overrideContext?.readyMinutes ? ` (${overrideContext.readyMinutes} min KPI)` : ''}.
                </p>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="override" checked={action === 'pass'} onChange={() => setAction('pass')} />
                        Auto Pass (Manual Pass)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="override" checked={action === 'fail'} onChange={() => setAction('fail')} />
                        Auto Fail (Manual Fail)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="override" checked={action === 'ignore'} onChange={() => setAction('ignore')} />
                        Ignore (Exclude from SLA)
                    </label>
                    <div style={{ marginTop: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#374151' }}>
                            Reason (required)
                        </label>
                        <input
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={action === 'ignore' ? 'e.g. Booking not applicable to SLA' : action === 'pass' ? 'e.g. Resource on standby before schedule' : 'e.g. No resource at gate'}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '14px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isPatching && (
                        <span style={{ marginRight: '8px', fontSize: '13px', color: '#6b7280' }}>Updating...</span>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isPatching}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: isPatching ? 'not-allowed' : 'pointer',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            opacity: isPatching ? 0.6 : 1,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={isPatching || !canApply}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: isPatching ? 'not-allowed' : 'pointer',
                            backgroundColor: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            opacity: isPatching ? 0.8 : 1,
                        }}
                    >
                        {isPatching ? 'Updating...' : 'Apply'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const groupByFlight = (rawData) => {
    const map = {};
    (rawData || []).forEach((row) => {
        const fid = row.flightId || 'unknown';
        if (!map[fid]) map[fid] = { ...row, passengerCount: 0 };
        map[fid].passengerCount += 1;
    });
    return Object.values(map);
};

const isNoDataRow = (row) => row.passReason === 'No Boarding';

/** Maps report KPI key to per-KPI override key */
const REPORT_KPI_TO_OVERRIDE_KEY = {
  lt230SeatsA: 'lt230_35',
  lt230SeatsB: 'lt230_30',
  gte230SeatsA: 'gte230_50',
  gte230SeatsB: 'gte230_45',
  firstWave: 'firstWave_35',
};

const KpiRawDataSection = ({ title, minutes, rawData, date, reportKpiKey, onOpenOverrideModal, breakdownView = 'passenger', includeNoData = false }) => {
    const [isOpen, setIsOpen] = useState(false);

    const filteredRawData = includeNoData ? (rawData || []) : (rawData || []).filter((row) => !isNoDataRow(row));
    const displayData = breakdownView === 'flight' ? groupByFlight(filteredRawData) : filteredRawData;
    const columnWidths = breakdownView === 'flight' ? columnWidthsFlight : columnWidthsPassenger;

    if (!displayData?.length) {
        return (
            <div>
                <p style={subTitleStyle}>{title} ({minutes} min) {date ? `(${date})` : ''}</p>
                <p style={{ fontSize: '13px', color: '#9ca3af' }}>No records.</p>
            </div>
        );
    }

    const containerStyle = {
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: '#fff',
    };

    return (
        <div style={containerStyle}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <span style={{
                    display: 'inline-block',
                    fontSize: '12px',
                    color: '#9ca3af',
                    transition: 'transform 0.2s ease',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                }}>
                    &#9654;
                </span>
                <p style={{ ...subTitleStyle, marginTop: 0, marginBottom: 0 }}>{title} ({minutes} min) ({displayData.length}) {date ? `(${date})` : ''}</p>
            </div>

            {!isOpen ? null : (<>
            <table style={{ ...tableStyle, borderBottom: 'none', marginTop: '10px' }}>
                <ColGroup widths={columnWidths} />
                <thead>
                    <tr>
                        {breakdownView === 'passenger' && (
                            <>
                                <th style={headerStyle}>Passenger Name</th>
                                <th style={headerStyle}>Passenger ID</th>
                                <th style={headerStyle}>Assistance</th>
                            </>
                        )}
                        <th style={headerStyle}>Flight</th>
                        <th style={headerStyle}>Scheduled Departure</th>
                        <th style={headerStyle}>Required Boarding</th>
                        <th style={headerStyle}>First Resource At Location</th>
                        <th style={headerStyle}>First Boarding</th>
                        <th style={headerStyle}>Earliest Boarding Allocated</th>
                        <th style={{ ...headerStyle, textAlign: 'center' }}>Ready (min before)</th>
                        <th style={headerStyle}>Inbound On Blocks</th>
                        {breakdownView === 'flight' && <th style={{ ...headerStyle, textAlign: 'center' }}>Pax</th>}
                        <th style={{ ...headerStyle, textAlign: 'center' }}>Pass</th>
                        <th style={headerStyle}>Reason</th>
                        <th style={{ ...headerStyle, textAlign: 'center' }}>Action</th>
                    </tr>
                </thead>
            </table>
            <div style={{ maxHeight: '400px', overflowY: 'auto', borderTop: 'none' }}>
                <table style={tableStyle}>
                    <ColGroup widths={columnWidths} />
                    <tbody>
                        {displayData.map((row, idx) => (
                            <tr key={row.bookingId || row.flightId + '-' + idx}>
                                {breakdownView === 'passenger' && (
                                    <>
                                        <td style={cellStyle}>{row.passengerName ?? '–'}</td>
                                        <td style={{ ...cellStyle, fontSize: '11px', wordBreak: 'break-all' }}>{row.passengerId ?? '–'}</td>
                                        <td style={cellStyle}>{row.assistance ?? '–'}</td>
                                    </>
                                )}
                                <td style={cellStyle}>{row.flightNumber}</td>
                                <td style={cellStyle}>{formatTime(row.scheduledDepartureTime)}</td>
                                <td style={cellStyle}>{formatTime(row.requiredBoardingTime)}</td>
                                <td style={cellStyle}>{formatTime(row.firstResourceAtLocation)}</td>
                                <td style={cellStyle}>{formatTime(row.firstBoardingTime)}</td>
                                <td style={cellStyle}>{formatTime(row.earliestTaskCreatedTime)}</td>
                                <td style={{ ...cellStyle, textAlign: 'center' }}>
                                    {row.readyMinutesBefore != null ? row.readyMinutesBefore.toFixed(1) : '-'}
                                </td>
                                <td style={cellStyle}>{formatTime(row.inboundActualOnBlocks)}</td>
                                {breakdownView === 'flight' && (
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>{row.passengerCount ?? 1}</td>
                                )}
                                <td style={{
                                    ...cellStyle,
                                    textAlign: 'center',
                                    backgroundColor: row.isIgnored ? '#fef9c3' : row.passReason === 'No Boarding' ? '#dbeafe' : row.pass ? '#dcfce7' : '#fee2e2',
                                    color: row.isIgnored ? '#854d0e' : row.passReason === 'No Boarding' ? '#1d4ed8' : row.pass ? '#166534' : '#991b1b',
                                }}>
                                    {row.isIgnored ? 'Ignored' : row.passReason === 'No Boarding' ? 'Skip' : row.pass ? (row.isManualOverride ? 'Pass (Manual)' : 'Pass') : (row.isManualOverride ? 'Fail (Manual)' : 'Fail')}
                                </td>
                                <td style={cellStyle}>{row.passReason || '-'}</td>
                                <td style={{ ...cellStyle, textAlign: 'center' }}>
                                    {onOpenOverrideModal && row.bookingId && breakdownView === 'passenger' ? (
                                        <button
                                            type="button"
                                            onClick={() => onOpenOverrideModal(row, date, REPORT_KPI_TO_OVERRIDE_KEY[reportKpiKey] || reportKpiKey, minutes)}
                                            style={{
                                                padding: '6px 12px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                backgroundColor: '#3b82f6',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '6px',
                                            }}
                                        >
                                            Action
                                        </button>
                                    ) : '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            </>)}
        </div>
    );
};

const BoardingSlaRawDataTable = ({ dailyReports, overrideContext, onOverrideRowChange, onPatchBoarding, isPatching, includeNoData = false }) => {
    const [breakdownView, setBreakdownView] = useState('passenger');

    if (!dailyReports?.length) return null;

    const handleOpenOverrideModal = (row, operationDate, kpiKey, readyMinutes) => {
        onOverrideRowChange?.({ row, operationDate, kpiKey, readyMinutes });
    };

    return (
        <div style={{ marginTop: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>Breakdown view:</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                        type="radio"
                        name="breakdownView"
                        checked={breakdownView === 'passenger'}
                        onChange={() => setBreakdownView('passenger')}
                    />
                    Passenger (name + assistance)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                        type="radio"
                        name="breakdownView"
                        checked={breakdownView === 'flight'}
                        onChange={() => setBreakdownView('flight')}
                    />
                    Flight
                </label>
            </div>
            {dailyReports.map((report) => {
                const kpiKeys = report.kpis ? Object.keys(report.kpis) : [];

                return (
                    <div key={report.date} style={{ marginBottom: '24px' }}>
                        {kpiKeys.map((key) => {
                            const kpi = report.kpis[key];
                            return (
                                <KpiRawDataSection
                                    key={key}
                                    reportKpiKey={key}
                                    title={kpi.title}
                                    minutes={kpi.minutes}
                                    rawData={kpi.rawData}
                                    date={report.date}
                                    onOpenOverrideModal={handleOpenOverrideModal}
                                    breakdownView={breakdownView}
                                    includeNoData={includeNoData}
                                />
                            );
                        })}
                    </div>
                );
            })}
            <BoardingOverrideModal
                overrideContext={overrideContext}
                open={!!overrideContext}
                onClose={() => onOverrideRowChange?.(null)}
                onApply={onPatchBoarding}
                isPatching={isPatching}
            />
        </div>
    );
};

export default BoardingSlaRawDataTable;
